import { NeonColors, neonButton, neonText, neonPanel, SCREEN_BASE_STYLE, fadeTransition } from '../neon-theme'
import { AdminDashboard } from '../admin/AdminDashboard'
import { AdminRoomManager } from '../admin/AdminRoomManager'
import { AdminPlayerManager } from '../admin/AdminPlayerManager'
import { AdminPoolBreakdown } from '../admin/AdminPoolBreakdown'
import { mountToAppShell } from '../appShell'

type TabId = 'dashboard' | 'rooms' | 'players' | 'pool'

interface TabDef {
  id: TabId
  label: string
  placeholder: string
}

interface Disposable {
  dispose(): void
}

const TABS: readonly TabDef[] = [
  { id: 'dashboard', label: 'Dashboard', placeholder: 'Dashboard content loading...' },
  { id: 'pool', label: 'Pool', placeholder: 'Pool breakdown loading...' },
  { id: 'rooms', label: 'Rooms', placeholder: 'Rooms panel loading...' },
  { id: 'players', label: 'Players', placeholder: 'Players panel loading...' },
] as const

export class AdminScreen {
  private container: HTMLDivElement
  private token: string | null = null
  private onBack: (() => void) | null = null
  private activeTab: TabId = 'dashboard'
  private activeComponent: Disposable | null = null
  private flashTimeout: number | null = null

  constructor() {
    this.container = document.createElement('div')
    this.container.id = 'admin-screen'
    this.container.style.cssText = `
      ${SCREEN_BASE_STYLE}
      overflow-y: auto;
      z-index: 210;
      opacity: 0;
      ${fadeTransition(300)}
    `
  }

  show(token: string, onBack: () => void): void {
    this.token = token
    this.onBack = onBack
    this.activeTab = 'dashboard'

    this.render()
    mountToAppShell(this.container)

    requestAnimationFrame(() => {
      this.container.style.opacity = '1'
    })

    this.mountTabComponent()
  }

  private render(): void {
    this.container.innerHTML = `
      <div style="
        width: 100%; max-width: 800px;
        padding: calc(env(safe-area-inset-top, 0px) + 28px) 20px calc(env(safe-area-inset-bottom, 0px) + 20px);
        display: flex; flex-direction: column; gap: 16px;
        min-height: 100%; box-sizing: border-box;
      ">
        <!-- Header -->
        <div style="
          display: flex; align-items: center; justify-content: space-between;
          margin-bottom: 8px;
        ">
          <h1 style="
            font-size: 1.5rem; font-weight: 900; letter-spacing: 2px; margin: 0;
            ${neonText(NeonColors.info, NeonColors.infoGlow)}
          ">ADMIN PANEL</h1>
          <div style="display: flex; gap: 8px; align-items: center;">
            <button id="admin-self-refund-btn" style="
              ${neonButton(NeonColors.gold, NeonColors.goldGlow)}
              padding: 8px 16px; font-size: 0.78rem;
            ">REFUND TG STARS</button>
            <button id="admin-self-free-games-btn" style="
              ${neonButton(NeonColors.success, NeonColors.successGlow)}
              padding: 8px 16px; font-size: 0.78rem;
            ">+5 FREE GAMES</button>
            <button id="admin-back-btn" style="
              ${neonButton(NeonColors.danger, NeonColors.dangerGlow)}
              padding: 8px 20px; font-size: 0.85rem;
            ">BACK</button>
          </div>
        </div>
        <div id="admin-flash" style="
          min-height: 18px;
          font-size: 0.78rem;
          color: ${NeonColors.textDim};
          margin-top: -6px;
          margin-bottom: 4px;
        "></div>

        <!-- Tab Bar -->
        <div id="admin-tab-bar" style="
          display: flex; gap: 4px; flex-wrap: wrap;
        ">
          ${TABS.map(tab => this.renderTab(tab)).join('')}
        </div>

        <!-- Content Area -->
        <div id="admin-content" style="
          ${neonPanel()}
          min-height: 300px; flex: 1;
        ">
        </div>
      </div>
    `

    this.bindEvents()
  }

  private renderTab(tab: TabDef): string {
    const isActive = tab.id === this.activeTab
    const bgColor = isActive ? NeonColors.info : 'transparent'
    const textColor = isActive ? NeonColors.bgDark : NeonColors.textMuted
    const borderColor = isActive ? NeonColors.info : NeonColors.textDim
    const glow = isActive ? `box-shadow: 0 0 12px ${NeonColors.infoGlow};` : ''
    const fontWeight = isActive ? 'bold' : 'normal'

    return `<button class="admin-tab" data-tab="${tab.id}" style="
      background: ${bgColor};
      color: ${textColor};
      border: 1px solid ${borderColor};
      border-radius: 8px;
      padding: 8px 16px;
      font-size: 0.85rem;
      font-weight: ${fontWeight};
      cursor: pointer;
      transition: all 0.2s ease;
      ${glow}
      flex: 1;
      min-width: 80px;
      text-align: center;
    ">${tab.label}</button>`
  }

  private bindEvents(): void {
    const backBtn = this.container.querySelector('#admin-back-btn') as HTMLButtonElement
    const selfRefundBtn = this.container.querySelector('#admin-self-refund-btn') as HTMLButtonElement | null
    const selfFreeGamesBtn = this.container.querySelector('#admin-self-free-games-btn') as HTMLButtonElement | null
    backBtn?.addEventListener('click', () => {
      this.onBack?.()
      this.hide()
    })
    selfRefundBtn?.addEventListener('click', () => {
      void this.refundSelfSpentStars()
    })
    selfFreeGamesBtn?.addEventListener('click', () => {
      void this.grantSelfFreeGames()
    })

    this.bindTabClicks()
  }

  private bindTabClicks(): void {
    const tabBtns = this.container.querySelectorAll('.admin-tab') as NodeListOf<HTMLButtonElement>
    tabBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const tabId = btn.dataset.tab as TabId
        if (tabId && tabId !== this.activeTab) {
          this.activeTab = tabId
          this.updateTabs()
          this.mountTabComponent()
        }
      })
    })
  }

  private updateTabs(): void {
    const tabBar = this.container.querySelector('#admin-tab-bar')
    if (!tabBar) return

    tabBar.innerHTML = TABS.map(tab => this.renderTab(tab)).join('')
    this.bindTabClicks()
  }

  private mountTabComponent(): void {
    // Dispose previous component
    if (this.activeComponent) {
      this.activeComponent.dispose()
      this.activeComponent = null
    }

    const contentArea = this.container.querySelector('#admin-content') as HTMLElement | null
    if (!contentArea || !this.token) return

    contentArea.innerHTML = ''

    switch (this.activeTab) {
      case 'dashboard': {
        const dashboard = new AdminDashboard(this.token)
        dashboard.render(contentArea)
        this.activeComponent = dashboard
        break
      }
      case 'rooms': {
        const roomManager = new AdminRoomManager(this.token)
        roomManager.render(contentArea)
        this.activeComponent = roomManager
        break
      }
      case 'players': {
        const playerManager = new AdminPlayerManager(this.token)
        playerManager.render(contentArea)
        this.activeComponent = playerManager
        break
      }
      case 'pool': {
        const poolBreakdown = new AdminPoolBreakdown(this.token)
        poolBreakdown.render(contentArea)
        this.activeComponent = poolBreakdown
        break
      }
    }
  }

  getToken(): string | null {
    return this.token
  }

  hide(): void {
    if (this.activeComponent) {
      this.activeComponent.dispose()
      this.activeComponent = null
    }
    if (this.flashTimeout !== null) {
      window.clearTimeout(this.flashTimeout)
      this.flashTimeout = null
    }
    this.container.style.opacity = '0'
    setTimeout(() => this.container.remove(), 300)
  }

  dispose(): void {
    if (this.activeComponent) {
      this.activeComponent.dispose()
      this.activeComponent = null
    }
    if (this.flashTimeout !== null) {
      window.clearTimeout(this.flashTimeout)
      this.flashTimeout = null
    }
    this.container.remove()
    this.token = null
    this.onBack = null
  }

  private async grantSelfFreeGames(): Promise<void> {
    if (!this.token) return
    try {
      const resp = await fetch('/api/admin/me/adjust-free-games', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: 5,
          reason: 'quick self grant from admin panel',
        }),
      })
      if (!resp.ok) {
        this.showFlash('Failed to grant free games', true)
        return
      }
      this.showFlash('Added 5 free games to your account')
    } catch {
      this.showFlash('Failed to grant free games', true)
    }
  }

  private async refundSelfSpentStars(): Promise<void> {
    if (!this.token) return
    try {
      const resp = await fetch('/api/admin/me/refund-telegram-stars', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json',
        },
      })
      const json = await resp.json().catch(() => null)
      if (!resp.ok || !json?.success) {
        this.showFlash(json?.error ?? 'Failed to refund Telegram stars', true)
        return
      }
      const refundedStars = Number(json.data?.refundedTelegramStars ?? 0)
      const reversedInternalCredit = Number(json.data?.reversedInternalCreditStars ?? 0)
      this.showFlash(
        refundedStars > 0 || reversedInternalCredit > 0
          ? `Refunded ${refundedStars} Telegram stars${reversedInternalCredit > 0 ? `, reversed ${reversedInternalCredit} mistaken internal stars` : ''}`
          : 'No refundable Telegram stars found',
      )
    } catch {
      this.showFlash('Failed to refund Telegram stars', true)
    }
  }

  private showFlash(message: string, isError: boolean = false): void {
    const flash = this.container.querySelector('#admin-flash') as HTMLDivElement | null
    if (!flash) return
    flash.textContent = message
    flash.style.color = isError ? NeonColors.danger : NeonColors.success
    if (this.flashTimeout !== null) {
      window.clearTimeout(this.flashTimeout)
    }
    this.flashTimeout = window.setTimeout(() => {
      flash.textContent = ''
      flash.style.color = NeonColors.textDim
      this.flashTimeout = null
    }, 3000)
  }
}
